import { useMemo } from "react"
import type { Address } from "viem"

import { useLSTStakerData } from "@/stake/hooks/useLSTStakerData"
import { useDelegateeEligibilityThresholdScore } from "@/governance/hooks/useDelegateeEligibilityThresholdScore"
import { getChainIdParams } from "@/web3/helpers/chainId"
import type { Organization } from "@/query/graphql/schema"
import { useKarmaStats } from "@/governance/hooks/useKarmaScore"

type UseUserDRSParams = {
  address?: Address | string
  organization: Organization
}

export const useUserDRS = ({ address, organization }: UseUserDRSParams) => {
  const {
    chainId,
    delegatesEligibilityOracleAddress,
    drsDefaultThreshold,
    uiConfig: { displayDRS },
  } = useLSTStakerData({
    orgId: organization.id,
  })

  const chainReference = chainId
    ? getChainIdParams(chainId).reference
    : undefined

  const { thresholdScore, isLoading: isLoadingThresholdScore } =
    useDelegateeEligibilityThresholdScore({
      contractAddress: delegatesEligibilityOracleAddress,
      chainId: chainReference,
      defaultValue: drsDefaultThreshold,
    })

  const { data: karmaStats, isLoading: isLoadingKarmaScore } = useKarmaStats({
    address: address as Address,
    config: {
      period: "90d",
    },
    karmaName: organization?.metadata?.karmaName ?? undefined,
  })

  const userDRS = useMemo(() => {
    if (address) {
      return karmaStats?.karmaScore ?? 0
    }

    return 0
  }, [karmaStats, address])

  const isDRSElegible = useMemo(() => {
    if (!address || thresholdScore === undefined) {
      return false
    }

    return userDRS >= thresholdScore
  }, [userDRS, thresholdScore, address])

  return {
    userDRS,
    isDRSElegible,
    isLoading: isLoadingKarmaScore || isLoadingThresholdScore,
    isDRSActive: displayDRS,
    thresholdScore,
  }
}
